import React, { useEffect, useMemo, useState } from 'react';
import {
  ledgerReportingApi,
  incomingReturnsApi,
  incomingPaymentsApi,
  LedgerMovementRow,
  IncomingProviderReturn,
  IncomingPayment,
} from '../api/payments';
import { adminApi, CurrencyDto } from '../api/admin';
import CompanyActivityReport from './CompanyActivityReport';

type ReportsTab = 'movements' | 'returns' | 'unmatched';

export function LegacyReportsPage() {
  const [movements, setMovements] = useState<LedgerMovementRow[]>([]);
  const [returns, setReturns] = useState<IncomingProviderReturn[]>([]);
  const [unmatched, setUnmatched] = useState<IncomingPayment[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [currencies, setCurrencies] = useState<CurrencyDto[]>([]);

  const [tab, setTab] = useState<ReportsTab>('movements');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLedgerLoading(true);
      setLedgerError(null);
      try {
        const [mRes, rRes, uRes] = await Promise.all([
          ledgerReportingApi.movements(400),
          incomingReturnsApi.list(100),
          incomingPaymentsApi.listUnmatched(100),
        ]);
        if (cancelled) return;
        setMovements(mRes.data);
        setReturns(rRes.data);
        setUnmatched(uRes.data);
      } catch (e: unknown) {
        if (!cancelled) setLedgerError(e instanceof Error ? e.message : 'Failed to load reports');
      } finally {
        if (!cancelled) setLedgerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    adminApi.getCurrencies().then((res) => setCurrencies(res.data || [])).catch(() => setCurrencies([]));
  }, []);

  const currencyCodeByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of currencies) {
      if (!c?.id || !c?.code) continue;
      map.set(c.id.toUpperCase(), c.code.toUpperCase());
      map.set(c.code.toUpperCase(), c.code.toUpperCase());
    }
    return map;
  }, [currencies]);

  const resolveCurrencyLabel = (raw: string | null | undefined) => {
    const value = (raw || '').trim();
    if (!value) return '';
    return currencyCodeByKey.get(value.toUpperCase()) || value.toUpperCase();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <p className="text-gray-600">Ledger, incoming queues, and client support requests</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => setTab('movements')}
          className={`px-3 py-1.5 text-sm rounded-lg ${tab === 'movements' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          Ledger movements
        </button>
        <button
          type="button"
          onClick={() => setTab('returns')}
          className={`px-3 py-1.5 text-sm rounded-lg ${tab === 'returns' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          Incoming returns ({returns.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('unmatched')}
          className={`px-3 py-1.5 text-sm rounded-lg ${tab === 'unmatched' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          Unmatched incoming ({unmatched.length})
        </button>
      </div>

      {ledgerLoading && <div className="text-gray-500">Loading…</div>}
      {ledgerError && <div className="text-red-600 text-sm">{ledgerError}</div>}

      {!ledgerLoading && tab === 'movements' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Time (UTC)</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Operation</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Object</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Account</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Side</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Amount</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">CCY</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {movements.map((row) => (
                <tr key={`${row.transactionId}-${row.entryId}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                    {new Date(row.transactionCreatedAt).toISOString().replace('T', ' ').slice(0, 19)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-800">{row.operationType}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">
                    {row.businessObjectType}
                    {row.businessObjectId ? ` · ${String(row.businessObjectId).slice(0, 8)}…` : ''}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{String(row.accountId).slice(0, 8)}…</td>
                  <td className="px-3 py-2">{row.entrySide}</td>
                  <td className="px-3 py-2 text-right font-mono">{row.amount.toFixed(2)}</td>
                  <td className="px-3 py-2">{row.currencyCode}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{row.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {movements.length === 0 && !ledgerError && <p className="p-4 text-gray-500 text-sm">No movements yet.</p>}
        </div>
      )}

      {!ledgerLoading && tab === 'returns' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Created</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Provider</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Amount</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Original outgoing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {returns.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2">{new Date(r.createdAt).toISOString().slice(0, 10)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{String(r.providerId).slice(0, 8)}…</td>
                  <td className="px-3 py-2 font-mono">{r.amount.toFixed(2)} {resolveCurrencyLabel(r.currencyId)}</td>
                  <td className="px-3 py-2">{r.status}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.originalOutgoingPaymentId ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!ledgerLoading && tab === 'unmatched' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Registered</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Provider</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Amount</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Ref</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {unmatched.map((u) => (
                <tr key={u.id}>
                  <td className="px-3 py-2">{new Date(u.registeredAt).toISOString().slice(0, 10)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{String(u.providerId).slice(0, 8)}…</td>
                  <td className="px-3 py-2 font-mono">{u.amount.toFixed(2)} {resolveCurrencyLabel(u.currencyId)}</td>
                  <td className="px-3 py-2 text-xs">{u.providerReference ?? '—'}</td>
                  <td className="px-3 py-2">{u.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {unmatched.length === 0 && <p className="p-4 text-gray-500 text-sm">No unmatched items.</p>}
        </div>
      )}

    </div>
  );
}

function ReportsPage() {
  return <CompanyActivityReport />;
}

export default ReportsPage;
